import { comoCampo } from "@/componentes/tesoreria/conversion"
import { aCentimos } from "@/componentes/tesoreria/dinero"
import type { MontoDivisa } from "@/componentes/ui/TarjetaKpi"

/**
 * Los indicadores de Talento humano, Planificación estratégica y Nómina que se cuentan en
 * el NAVEGADOR. Funciones puras: reciben lo que la pestaña ya trae y devuelven cifras.
 *
 * ⚠ **Sólo se cuenta aquí lo que llega ENTERO.** Son catálogos de decenas de filas sin
 * paginar —cargos, áreas, procesos, plantillas, conceptos—, como «Gastos y categorías».
 * Personas (paginada) y los totales de nómina (viven en los recibos) se cuentan en el
 * servidor: `/personas/indicadores` y `/nomina/indicadores`.
 *
 * ⚠ **El dinero se suma en céntimos y POR MONEDA**, con los ayudantes de tesorería; nunca
 * un total entre monedas.
 *
 * Los tipos de aquí son MÍNIMOS a propósito: lo que cada cálculo lee, no la fila entera,
 * para que una columna nueva en la pantalla no obligue a tocar los indicadores.
 */

export function porMoneda(
  items: readonly { divisa: string | null; monto: string }[],
): MontoDivisa[] {
  const bolsa = new Map<string, bigint>()
  for (const i of items) {
    const divisa = i.divisa ?? "—"
    bolsa.set(divisa, (bolsa.get(divisa) ?? 0n) + aCentimos(i.monto))
  }
  return [...bolsa]
    .filter(([, c]) => c !== 0n)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([divisa, c]) => ({ divisa, monto: comoCampo(c) }))
}

const contar = <T>(filas: readonly T[], condicion: (f: T) => boolean) =>
  filas.filter(condicion).length

/** «YYYY-MM-DD» + días, en la hora local: para las ventanas «en los próximos 30 días». */
export function sumarDias(iso: string, dias: number): string {
  const [a, m, d] = iso.split("-").map(Number)
  const f = new Date(a ?? 2000, (m ?? 1) - 1, (d ?? 1) + dias)
  return `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, "0")}-${String(f.getDate()).padStart(2, "0")}`
}

// --- Talento humano ------------------------------------------------------------------

export type ContratoNominaMin = {
  sueldo_base: string
  divisa: string | null
  frecuencia: string
  vigente_hasta: string | null
  conceptos: unknown[]
}

export function kpiContratos(contratos: readonly ContratoNominaMin[], hoy: string) {
  const limite = sumarDias(hoy, 30)
  return {
    vivos: contratos.length,
    semanales: contratos.filter((c) => c.frecuencia === "SEMANAL").length,
    quincenales: contratos.filter((c) => c.frecuencia === "QUINCENAL").length,
    mensuales: contratos.filter((c) => c.frecuencia === "MENSUAL").length,
    porPago: porMoneda(contratos.map((c) => ({ divisa: c.divisa, monto: c.sueldo_base }))),
    vencen: contar(contratos, (c) => c.vigente_hasta !== null && c.vigente_hasta <= limite),
    sinConceptos: contar(contratos, (c) => c.conceptos.length === 0),
  }
}

export type DocumentoTHMin = { estado: string; creado_en: string; tiene_firmado: boolean }

export function kpiDocumentosTH(documentos: readonly DocumentoTHMin[], hoy: string) {
  const mes = hoy.slice(0, 7)
  return {
    porResolver: contar(documentos, (d) => d.estado === "SOLICITADO"),
    emitidosMes: contar(
      documentos,
      (d) => d.estado !== "ANULADO" && d.estado !== "RECHAZADO" && d.creado_en.slice(0, 7) === mes,
    ),
    sinFirmado: contar(documentos, (d) => d.estado === "EMITIDO" && !d.tiene_firmado),
    anulados: contar(documentos, (d) => d.estado === "ANULADO" || d.estado === "RECHAZADO"),
  }
}

export type EventoMin = { tipo: string; persona_id: number }

export function kpiAgenda(eventos: readonly EventoMin[]) {
  const de = (tipo: string) => eventos.filter((e) => e.tipo === tipo)
  return {
    salen: de("VACACIONES_INICIO").length,
    nacen: de("VACACIONES_NACE").length,
    utilidades: de("UTILIDADES_PAGO").length,
    garantias: de("GARANTIA").length,
    personas: new Set(eventos.map((e) => e.persona_id)).size,
  }
}

export type PlantillaMin = { activa: boolean; uso: string; requiere_aprobacion: boolean }

export function kpiPlantillas(plantillas: readonly PlantillaMin[]) {
  const activas = plantillas.filter((p) => p.activa)
  return {
    activas: activas.length,
    autoservicio: contar(activas, (p) => p.uso === "EMPLEADO"),
    conAprobacion: contar(activas, (p) => p.requiere_aprobacion),
    archivadas: plantillas.length - activas.length,
  }
}

// --- Planificación estratégica ----------------------------------------------------------

export type PilarMin = {
  objetivos: { vencido: boolean; avance: number; etapas: { estado: string }[] }[]
}

export function kpiEstrategia(pilares: readonly PilarMin[]) {
  const objetivos = pilares.flatMap((p) => p.objetivos)
  const etapas = objetivos.flatMap((o) => o.etapas)
  return {
    pilares: pilares.length,
    pilaresVacios: contar(pilares, (p) => p.objetivos.length === 0),
    objetivos: objetivos.length,
    vencidos: contar(objetivos, (o) => o.vencido),
    cumplidos: contar(objetivos, (o) => o.avance >= 100),
    etapas: etapas.length,
    completadas: contar(etapas, (e) => e.estado === "COMPLETADA"),
  }
}

export type NodoMin = { vacante: boolean; persona_id: number | null }

export function kpiOrganigrama(nodos: readonly NodoMin[]) {
  const vacantes = contar(nodos, (n) => n.vacante || n.persona_id === null)
  return {
    posiciones: nodos.length,
    vacantes,
    cubiertas: nodos.length - vacantes,
    personas: new Set(nodos.map((n) => n.persona_id).filter((p) => p !== null)).size,
  }
}

export type DepartamentoMin = {
  activo: boolean
  cargos: number
  cargo_responsable_id: number | null
  responsable_ocupante: string | null
}

export function kpiDepartamentos(departamentos: readonly DepartamentoMin[]) {
  const activos = departamentos.filter((d) => d.activo)
  return {
    activos: activos.length,
    sinResponsable: contar(activos, (d) => d.cargo_responsable_id === null),
    responsableVacante: contar(
      activos,
      (d) => d.cargo_responsable_id !== null && !d.responsable_ocupante,
    ),
    sinCargos: contar(activos, (d) => d.cargos === 0),
    cargos: activos.reduce((s, d) => s + d.cargos, 0),
  }
}

export type CargoMin = {
  activo: boolean
  personas: number
  departamento_id: number | null
  funciones: unknown[]
}

export function kpiCargos(cargos: readonly CargoMin[]) {
  const activos = cargos.filter((c) => c.activo)
  return {
    activos: activos.length,
    ocupados: contar(activos, (c) => c.personas > 0),
    sinOcupante: contar(activos, (c) => c.personas === 0),
    sinFunciones: contar(activos, (c) => c.funciones.length === 0),
    sinArea: contar(activos, (c) => c.departamento_id === null),
  }
}

export type ProcesoMin = {
  activo: boolean
  es_transversal: boolean
  documento_id: number | null
  responsable_cargo_id: number | null
  pasos: unknown[]
}

export function kpiProcesos(procesos: readonly ProcesoMin[]) {
  const activos = procesos.filter((p) => p.activo)
  return {
    activos: activos.length,
    transversales: contar(activos, (p) => p.es_transversal),
    sinResponsable: contar(activos, (p) => p.responsable_cargo_id === null),
    sinDocumento: contar(activos, (p) => p.documento_id === null),
    sinPasos: contar(activos, (p) => p.pasos.length === 0),
  }
}

export type DocumentoSgiMin = { estado: string; versiones_publicadas: number }

export function kpiBiblioteca(documentos: readonly DocumentoSgiMin[]) {
  return {
    total: documentos.length,
    vigentes: contar(documentos, (d) => d.estado === "VIGENTE"),
    borradores: contar(documentos, (d) => d.estado === "BORRADOR"),
    obsoletos: contar(documentos, (d) => d.estado === "OBSOLETO"),
    sinPublicar: contar(documentos, (d) => d.estado !== "OBSOLETO" && d.versiones_publicadas === 0),
  }
}

// --- Nómina ---------------------------------------------------------------------------

export type ConceptoMin = { tipo: string; forma_calculo: string; activo: boolean }

export function kpiConceptos(conceptos: readonly ConceptoMin[]) {
  const activos = conceptos.filter((c) => c.activo)
  return {
    asignaciones: contar(activos, (c) => c.tipo === "ASIGNACION"),
    deducciones: contar(activos, (c) => c.tipo === "DEDUCCION"),
    porcentuales: contar(activos, (c) => c.forma_calculo === "PORCENTAJE_SUELDO"),
    archivados: conceptos.length - activos.length,
  }
}

// --- Tesorería (03/11) -----------------------------------------------------------------

export type OrdenPorPagarMin = {
  proveedor_id: number
  divisa: string | null
  monto_neto: string
  monto_pendiente: string
  fecha_requerida: string | null
}

/** Las aprobadas por pagar: lo que queda de cada una, que es lo que va a salir. */
export function kpiPagosPorEjecutar(ordenes: readonly OrdenPorPagarMin[], hoy: string) {
  const resto = (o: OrdenPorPagarMin) => ({
    divisa: o.divisa,
    monto: o.monto_pendiente || o.monto_neto,
  })
  const bolsa = (filas: readonly OrdenPorPagarMin[]) => ({
    cuentas: filas.length,
    montos: porMoneda(filas.map(resto)),
  })
  const semana = sumarDias(hoy, 7)
  return {
    todas: bolsa(ordenes),
    proveedores: new Set(ordenes.map((o) => o.proveedor_id)).size,
    vencidas: bolsa(ordenes.filter((o) => o.fecha_requerida !== null && o.fecha_requerida < hoy)),
    semana: bolsa(
      ordenes.filter(
        (o) =>
          o.fecha_requerida !== null && o.fecha_requerida >= hoy && o.fecha_requerida <= semana,
      ),
    ),
    parciales: bolsa(
      ordenes.filter(
        (o) =>
          Boolean(o.monto_pendiente) && aCentimos(o.monto_pendiente) !== aCentimos(o.monto_neto),
      ),
    ),
  }
}

export type SaldoProveedorMin = {
  saldos: { divisa: string; a_favor_proveedor: string; disponible_empresa: string; neto: string }[]
}

/** Los dos lados del saldo de proveedores, por moneda y nunca sumados entre sí. */
export function kpiSaldosProveedores(proveedores: readonly SaldoProveedorMin[]) {
  const lado = (campo: "a_favor_proveedor" | "disponible_empresa") => {
    const con = proveedores.filter((p) => p.saldos.some((s) => aCentimos(s[campo]) !== 0n))
    return {
      proveedores: con.length,
      montos: porMoneda(
        proveedores.flatMap((p) => p.saldos.map((s) => ({ divisa: s.divisa, monto: s[campo] }))),
      ),
    }
  }
  const aFavor = lado("a_favor_proveedor")
  const disponible = lado("disponible_empresa")
  return {
    aFavor,
    disponible,
    conSaldo: contar(proveedores, (p) => p.saldos.some((s) => aCentimos(s.neto) !== 0n)),
    neto: porMoneda(
      proveedores.flatMap((p) => p.saldos.map((s) => ({ divisa: s.divisa, monto: s.neto }))),
    ),
    ambos: contar(
      proveedores,
      (p) =>
        p.saldos.some((s) => aCentimos(s.a_favor_proveedor) !== 0n) &&
        p.saldos.some((s) => aCentimos(s.disponible_empresa) !== 0n),
    ),
  }
}

/** Los anticipos de clientes: ingresado y todavía sin aplicar a una CxC. */
export function kpiAnticiposClientes(
  cuentas: readonly { saldos: { divisa: string; anticipo: string }[] }[],
) {
  const con = cuentas.filter((c) => c.saldos.some((s) => aCentimos(s.anticipo) > 0n))
  return {
    clientes: con.length,
    montos: porMoneda(
      con.flatMap((c) => c.saldos.map((s) => ({ divisa: s.divisa, monto: s.anticipo }))),
    ),
  }
}

/** Lo que deben los empleados por anticipos y préstamos; lo devuelto de más no resta aquí. */
export function kpiDeudaEmpleados(
  cuentas: readonly { saldos: { divisa: string; debe: string }[] }[],
) {
  const deben = cuentas.flatMap((c) => c.saldos.filter((s) => aCentimos(s.debe) > 0n))
  return {
    empleados: contar(cuentas, (c) => c.saldos.some((s) => aCentimos(s.debe) > 0n)),
    montos: porMoneda(deben.map((s) => ({ divisa: s.divisa, monto: s.debe }))),
  }
}
